import { FormGroup } from "@blueprintjs/core";
import {
  ItemMultiSelect,
  ItemSelect,
  MediaImage,
} from "@package/ui-apollo-components";
import {
  GetLayoutGridDocument,
  GetLocationListNameIdDocument,
  GetMediaGridDocument,
  GetPostListNameIdDocument,
  GetProfileListNameIdDocument,
  GetServiceListNameIdDocument,
  GetTagListNameIdDocument,
  ServiceAppCmsReferenceTypes,
  type ExtraFieldTypeReference,
} from "@package/ui-gql/index-data-access";
import type { DocumentNode } from "graphql";
import { get, set, uniq } from "lodash";
import { useMemo } from "react";
import { MediaTagSelector } from "../../MediaTagSelector/MediaTagSelector";
import type { ServiceDataFormFieldBaseProps } from "./ServiceDataFormFieldEmpty";

type ServiceDataFormFieldReferenceProps = ServiceDataFormFieldBaseProps & {
  field: ExtraFieldTypeReference;
};

const serviceDataReferenceTypeToDocument: {
  [key in ServiceAppCmsReferenceTypes]: DocumentNode;
} = {
  Layout: GetLayoutGridDocument,
  Location: GetLocationListNameIdDocument,
  Media: GetMediaGridDocument,
  Post: GetPostListNameIdDocument,
  Profile: GetProfileListNameIdDocument,
  Service: GetServiceListNameIdDocument,
  Tag: GetTagListNameIdDocument,
};

function ServiceDataFormFieldReferenceContentSingle({
  data,
  field,
  onSourceChanged,
}: ServiceDataFormFieldReferenceProps) {
  const disabled = useMemo<boolean>(
    () => field.disabled ?? false,
    [field.disabled],
  );
  const selectedItemId = useMemo<string | null | undefined>(
    () => get(data, field.source),
    [data, field.source],
  );

  if (field.reference === ServiceAppCmsReferenceTypes.Media) {
    return (
      <>
        <FormGroup label={field.label ?? "Media"} labelFor="control-media">
          <div className="mb-2">
            <MediaTagSelector
              fill
              large
              minimal
              id={selectedItemId ?? undefined}
              onRemoveMedia={() =>
                onSourceChanged((draft) => {
                  set(draft, field.source, undefined);
                })
              }
              onSelectMedia={(id) =>
                onSourceChanged((draft) => {
                  set(draft, field.source, id);
                })
              }
            />
          </div>
          <MediaImage id={selectedItemId ?? undefined} />
        </FormGroup>
      </>
    );
  }

  return (
    <FormGroup disabled={disabled} label={field.label}>
      <ItemSelect
        activeItemId={selectedItemId}
        disabled={disabled}
        document={serviceDataReferenceTypeToDocument[field.reference]}
        nullable={!!field.nullable}
        onItemSelected={(id) =>
          onSourceChanged((draft) => {
            set(draft, field.source, id);
          })
        }
        variables={{
          filter: field.filter,
        }}
      />
    </FormGroup>
  );
}
function ServiceDataFormFieldReferenceContentMultiple({
  data,
  field,
  onSourceChanged,
}: ServiceDataFormFieldReferenceProps) {
  const disabled = useMemo<boolean>(
    () => field.disabled ?? false,
    [field.disabled],
  );
  const selectedItemIds = useMemo<string[]>(() => {
    const ids = get(data, field.source);
    if (Array.isArray(ids)) {
      return ids;
    }

    return [];
  }, [data, field.source]);

  return (
    <FormGroup disabled={disabled} label={field.label}>
      <ItemMultiSelect
        activeItemIds={selectedItemIds}
        disabled={disabled}
        document={serviceDataReferenceTypeToDocument[field.reference]}
        nullable={!!field.nullable}
        onItemSelected={(id) =>
          onSourceChanged((draft) => {
            if (id) {
              set(draft, field.source, uniq(selectedItemIds.concat(id)));
            }
          })
        }
        onItemRemoved={(id) => {
          onSourceChanged((draft) => {
            if (id) {
              set(
                draft,
                field.source,
                uniq(selectedItemIds.filter((i) => i !== id)),
              );
            }
          });
        }}
        variables={{
          filter: field.filter,
        }}
      />
    </FormGroup>
  );
}

function ServiceDataFormFieldReferenceContent({
  data,
  field,
  onSourceChanged,
}: ServiceDataFormFieldReferenceProps) {
  if (field.multiple) {
    return (
      <ServiceDataFormFieldReferenceContentMultiple
        field={field}
        onSourceChanged={onSourceChanged}
        data={data}
      />
    );
  }

  return (
    <ServiceDataFormFieldReferenceContentSingle
      field={field}
      onSourceChanged={onSourceChanged}
      data={data}
    />
  );
}

export function ServiceDataFormFieldReference({
  field,
  onSourceChanged,
  data,
}: ServiceDataFormFieldBaseProps) {
  if (field.__typename === "ExtraFieldTypeReference") {
    return (
      <ServiceDataFormFieldReferenceContent
        field={field}
        data={data}
        onSourceChanged={onSourceChanged}
      />
    );
  }

  return null;
}
